/** The reporting tree, built from the public directory projection (`DirectoryEntry`, served to anyone signed in). Nothing confidential goes into the shape of the tree. */

// Type-only: a runtime import of lib/hr pulls in the api client (and `window`), which would stop this module loading in a node test.
import type { DirectoryEntry } from './hr'

/** `lib/hr.ts::personName`, repeated so this module stays DOM-free. */
function personName(p: Pick<DirectoryEntry, 'first_name' | 'last_name' | 'display_name'>): string {
  return `${p.first_name} ${p.last_name}`.trim() || p.display_name
}

export interface OrgNode {
  person: DirectoryEntry
  reports: OrgNode[]
  /** People anywhere below this one, not just direct reports. */
  total: number
}

function byName(a: DirectoryEntry, b: DirectoryEntry) {
  return personName(a).localeCompare(personName(b))
}

/** Nest the flat rows by `supervisor_id`. The column is typed by hand, so it can dangle, point at self, or form a cycle. Dangling or self makes the person a root; whatever the walk did not reach (a cycle) is re-seeded as a root, so the chart stays complete even when the data is wrong. */
export function buildOrgTree(people: DirectoryEntry[]): OrgNode[] {
  const byId = new Map(people.map((p) => [p.id, p]))
  const childrenOf = new Map<string, DirectoryEntry[]>()
  const roots: DirectoryEntry[] = []

  for (const person of people) {
    const supervisor =
      person.supervisor_id && person.supervisor_id !== person.id && byId.has(person.supervisor_id)
        ? person.supervisor_id
        : null
    if (!supervisor) {
      roots.push(person)
      continue
    }
    const siblings = childrenOf.get(supervisor) ?? []
    siblings.push(person)
    childrenOf.set(supervisor, siblings)
  }

  const built = new Set<string>()
  function build(person: DirectoryEntry): OrgNode {
    built.add(person.id)
    const reports = (childrenOf.get(person.id) ?? [])
      .filter((child) => !built.has(child.id))
      .sort(byName)
      .map(build)
    return { person, reports, total: reports.reduce((n, r) => n + 1 + r.total, 0) }
  }

  const nodes = roots.sort(byName).map(build)
  for (const person of people.slice().sort(byName)) {
    if (!built.has(person.id)) nodes.push(build(person))
  }
  return nodes
}

/** Lower-cased, accent-folded, so "Ölzii" is found by "olzii". */
function fold(text: string): string {
  return text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()
}

/** Every searchable string of one person: both scripts of the name, the position in each language, the team and the staff code. */
function haystack(p: DirectoryEntry): string {
  return fold(
    [
      personName(p),
      p.display_name,
      p.first_name,
      p.last_name,
      p.first_name_mn,
      p.last_name_mn,
      p.position,
      p.position_mn,
      p.team,
      p.staff_code,
    ]
      .filter(Boolean)
      .join(' '),
  )
}

interface OrgSearch {
  /** The people the query matched, in tree (depth-first) order. */
  matches: string[]
  /** Every node that must be open for each match to be visible: the matches' ancestors. */
  expand: Set<string>
}

/** Find people in the BUILT tree, so a cycle or dangling id resolves exactly as `buildOrgTree` drew it. A blank query finds nobody; every word must appear, in any order. */
export function searchOrgTree(nodes: OrgNode[], query: string): OrgSearch {
  const words = fold(query).split(/\s+/).filter(Boolean)
  const matches: string[] = []
  const expand = new Set<string>()
  if (words.length === 0) return { matches, expand }

  function walk(node: OrgNode, path: string[]) {
    const text = haystack(node.person)
    if (words.every((w) => text.includes(w))) {
      matches.push(node.person.id)
      for (const id of path) expand.add(id)
    }
    for (const report of node.reports) walk(report, [...path, node.person.id])
  }
  for (const node of nodes) walk(node, [])
  return { matches, expand }
}

/** One or two letters for an avatar, from the displayed name. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = Array.from(parts[0]!)[0] ?? ''
  const last = parts.length > 1 ? (Array.from(parts[parts.length - 1]!)[0] ?? '') : ''
  return (first + last).toUpperCase()
}

/** Ids of every node down to `depth` levels (roots are depth 1): the default open state. */
export function openToDepth(nodes: OrgNode[], depth: number): Set<string> {
  const open = new Set<string>()
  function walk(node: OrgNode, level: number) {
    if (level >= depth || node.reports.length === 0) return
    open.add(node.person.id)
    for (const r of node.reports) walk(r, level + 1)
  }
  for (const node of nodes) walk(node, 1)
  return open
}
